import { stripeService } from '@/services/stripe';
import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { buildStripeKlarnaPayRequest } from '@/utils/klarna';
import type { AdyenPaymentResponse, StripePaymentResponse } from '@/types/payment';
import type { KlarnaPaymentParams } from '@/types/paymentHandlers';
import { handleAdyenPayment } from './adyenPaymentHandle';

// Stripe-specific Klarna handler
const handleStripeKlarna = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: KlarnaPaymentParams,
  klarnaSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse> => {
  const { elements } = params;

  const billingDetails = {
    email: checkout.customer.email,
    address: {
      country: checkout.customer.geoLocator.countryCode2,
    },
  };

  const paymentMethod = await stripeService.createKlarnaPaymentMethod(elements, billingDetails);

  const request = buildStripeKlarnaPayRequest(paymentMethod, klarnaSettings, checkout.customer.email || '');

  const response = await sendPaymentToServer<StripePaymentResponse>(request);
  return response;
};

// Main handler that routes based on provider
export const handleKlarnaPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params?: KlarnaPaymentParams
): Promise<StripePaymentResponse | AdyenPaymentResponse> => {
  if (!params) {
    throw new Error('Klarna payment requires elements parameter');
  }

  const klarnaSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.KLARNA
  );

  if (!klarnaSettings) {
    throw new Error('Klarna settings not found');
  }

  switch (klarnaSettings.provider) {
    case ProviderEnum.STRIPE:
      return await handleStripeKlarna(checkout, sendPaymentToServer, params, klarnaSettings);
    case ProviderEnum.ADYEN:
      return (await handleAdyenPayment(
        checkout,
        sendPaymentToServer,
        params as unknown as Record<string, string>
      )) as AdyenPaymentResponse;
    default:
      throw new Error(`Unsupported provider for Klarna: ${klarnaSettings.provider}`);
  }
};
